import { useEffect, useState } from "react";
import axios from "axios";
import AIStylistDrawer from "./AIStylistDrawer";
import { MAYA_PORTRAIT_URL } from "../../../lib/mayaPortrait";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * iter239 — Floating button that opens the AI Stylist drawer.
 * iter250 — Gated by `ai_stylist_enabled` admin flag (default OFF).
 *           When OFF, the FAB renders nothing — the storefront stays
 *           clean and the boutique doesn't pay Universal Key credits
 *           per customer turn.
 * iter278.4 — FAB now wears Maya's portrait instead of an anonymous
 *             Sparkles icon. Same illustration as the in-studio
 *             consultant panel — one face everywhere, so the customer
 *             builds a real relationship with the brand's stylist.
 *
 * Pairs with the WhatsApp FAB. The two FABs sit stacked vertically on
 * the bottom-right of the storefront so customers always have:
 *   • Maya — instant guided shopping
 *   • WhatsApp — human handoff for tricky asks
 */
export default function AIStylistFab() {
    const [open, setOpen] = useState(false);
    // null = still loading; we render nothing until we know.
    const [enabled, setEnabled] = useState(null);

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${API}/settings/public`)
            .then((r) => {
                if (cancelled) return;
                setEnabled(!!r?.data?.ai_stylist_enabled);
            })
            .catch(() => {
                // On error, default to OFF — keeps spend safe.
                if (!cancelled) setEnabled(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    if (!enabled) return null;

    return (
        <>
            {/* `bottom` tracks --jns-cta-bar-h (published by the sticky cart/checkout/PDP
                bars) so Maya lifts ABOVE the CTA instead of covering it; falls back
                to the normal 1.25rem when no bar is present. */}
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="ai-stylist-fab"
                data-nosnippet
                aria-label="Chat with Maya, JnS's in-house stylist"
                title="Maya · AI Stylist"
                style={{ bottom: "calc(var(--jns-cta-bar-h, 0px) + 1.25rem)" }}
                className="fixed right-5 sm:right-6 z-40 group inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full shadow-xl ring-2 ring-rosegold/70 hover:ring-rosegold hover:scale-110 transition-[bottom,transform] duration-300 bg-ivory"
            >
                {/* iter278.5 — Inner span owns the overflow-hidden + rounded
                    mask. The button itself is NOT clipped, so the green
                    "online" dot below can extend slightly outside the
                    portrait circle without being chopped. */}
                <span className="absolute inset-0 rounded-full overflow-hidden">
                    <img
                        src={MAYA_PORTRAIT_URL}
                        alt="Maya — your in-house stylist"
                        className="w-full h-full object-cover"
                        loading="eager"
                    />
                </span>
                {/* Soft halo ping to draw the eye on idle */}
                <span
                    className="absolute inset-0 rounded-full bg-rosegold/35 animate-ping pointer-events-none"
                    aria-hidden
                />
                {/* Tiny "online" dot — Maya is always available. Positioned
                    OUTSIDE the overflow-hidden mask so the full circle is
                    visible at the bottom-right edge of the button. */}
                <span
                    className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-emerald-500 ring-2 ring-ivory shadow z-10"
                    aria-label="Maya is online"
                />
                {/* Jul 2026 — label is now ALWAYS visible (was hover-only, which
                    means invisible on touch devices): first-time mobile customers
                    had no idea the portrait bubble was a stylist. Quiet pill at
                    rest, darkens on hover. */}
                <span
                    className="absolute right-full mr-3 px-2.5 py-1 rounded-full bg-ink/90 text-ivory text-[10px] font-bold uppercase tracking-[0.18em] shadow-md group-hover:bg-ink transition whitespace-nowrap pointer-events-none"
                    data-testid="maya-fab-label"
                >
                    Ask Maya ✨
                </span>
            </button>

            <AIStylistDrawer open={open} onClose={() => setOpen(false)} />
        </>
    );
}
